import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { AdminUser } from '../../../types/admin';

interface BanUserModalProps {
  open: boolean;
  user: AdminUser | null;
  onConfirm: (userId: string, reason: string) => Promise<{ success: boolean; error?: string }>;
  onCancel: () => void;
  isLoading?: boolean;
}

const PRESET_BAN_REASONS = [
  'Violation of Terms of Service',
  'Spam or abusive behavior',
  'Fraudulent activity detected',
  'Multiple user reports',
  'Payment fraud',
  'Inappropriate content',
  'Harassment of other users',
  'Account security concerns',
  'Custom reason...',
];

/**
 * BanUserModal - Confirmation modal for banning a user
 *
 * Features:
 * - Preset ban reasons dropdown
 * - Custom reason text input
 * - User information display
 * - Validation (reason required)
 * - Loading state during ban operation
 */
export default function BanUserModal({
  open,
  user,
  onConfirm,
  onCancel,
  isLoading = false,
}: BanUserModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');

  const handleConfirm = async () => {
    if (!user) return;

    const reason = selectedReason === 'Custom reason...' ? customReason : selectedReason;
    if (!reason.trim()) return;

    const result = await onConfirm(user.id, reason);
    if (result.success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onCancel();
  };

  const isCustomReason = selectedReason === 'Custom reason...';
  const isConfirmDisabled =
    isLoading ||
    !selectedReason ||
    (isCustomReason && !customReason.trim());

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Ban User Account
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            This will:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>Immediately revoke user access</li>
            <li>Prevent user from logging in</li>
            <li>Cancel active subscriptions</li>
            <li>Record this action in the audit log</li>
          </Typography>
        </Alert>

        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            User to ban:
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Ban Reason</InputLabel>
          <Select
            value={selectedReason}
            label="Ban Reason"
            onChange={(e) => setSelectedReason(e.target.value)}
            disabled={isLoading}
          >
            {PRESET_BAN_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isCustomReason && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Custom Ban Reason"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Enter detailed reason for banning this user..."
            disabled={isLoading}
            helperText="This reason will be recorded in the user's account and audit log"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isConfirmDisabled}
        >
          {isLoading ? 'Banning User...' : 'Ban User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
